import { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { FaMapMarkerAlt, FaTruck, FaBox, FaCheckCircle } from 'react-icons/fa';

const DeliveryTracker = ({ orderId }) => {
  const api = useApi();
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDelivery = async () => {
      try {
        const response = await api.get(`/delivery/track/${orderId}`);
        setDelivery(response.data);
      } catch (error) {
        console.error('Error fetching delivery', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDelivery();
  }, [orderId, api]);

  if (loading) return (
    <div className="flex items-center gap-4 text-xs tracking-widest uppercase text-textMuted p-8">
      <div className="w-4 h-4 border-2 border-gold border-t-transparent rounded-full animate-spin"></div> Interrogating Network...
    </div>
  );
  if (!delivery) return <div className="text-[10px] tracking-widest uppercase text-white/50 p-8">Logistics Data Unavailable</div>;

  const statusSteps = ['Pending', 'Processing', 'Out for Delivery', 'Delivered'];
  const currentStatusIndex = statusSteps.indexOf(delivery.status);

  return (
    <div className="w-full">
      <div className="relative mb-12 px-2">
        {/* Timeline Path */}
        <div className="absolute top-5 left-8 right-8 h-[1px] bg-white/10 z-0"></div>
        <div 
          className="absolute top-5 left-8 h-[1px] bg-gold z-0 transition-all duration-1000"
          style={{ width: `${(currentStatusIndex / (statusSteps.length - 1)) * 100}%`, maxWidth: 'calc(100% - 4rem)' }}
        ></div>

        <div className="flex justify-between relative z-10 hidden sm:flex">
          {statusSteps.map((step, index) => {
            const isCompleted = index <= currentStatusIndex;
            const isCurrent = index === currentStatusIndex;
            return (
              <div key={step} className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm transition-all duration-500 ${isCurrent ? 'bg-gold text-deepDark shadow-[0_0_15px_rgba(255,215,0,0.5)]' : isCompleted ? 'bg-gold text-deepDark opacity-70' : 'bg-[#0a0a0a] border border-white/20 text-white/30'}`}>
                  {index === 0 && <FaBox />}
                  {index === 1 && <FaTruck />}
                  {index === 2 && <FaTruck />}
                  {index === 3 && <FaCheckCircle />}
                </div>
                <span className={`text-[10px] tracking-widest uppercase mt-4 text-center ${isCompleted ? 'text-gold' : 'text-textMuted'}`}>
                  {step}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 border-t border-white/5 pt-8">
        <div>
          <div className="flex gap-4 mb-6">
            <FaMapMarkerAlt className="text-gold mt-1 flex-shrink-0" />
            <div>
              <p className="text-[10px] tracking-widest uppercase text-textMuted mb-1">Current Position</p>
              <p className="text-sm font-light text-white">{delivery.currentLocation?.address || 'In Transit'}</p>
            </div>
          </div>
          <div className="mb-6">
            <p className="text-[10px] tracking-widest uppercase text-textMuted mb-1">Service Level</p>
            <p className="text-sm font-light text-white">{delivery.courier?.name || 'Exclusive Black-Liner'}</p>
          </div>
        </div>

        <div>
          <div className="mb-6">
            <p className="text-[10px] tracking-widest uppercase text-textMuted mb-1">Estimated Arrival</p>
            <p className="text-lg font-serif text-white">{new Date(delivery.estimatedDeliveryDate).toLocaleDateString()}</p>
          </div>
          {delivery.deliveryAgent && (
            <div>
              <p className="text-[10px] tracking-widest uppercase text-textMuted mb-1">Assigned Agent</p>
              <p className="text-sm font-light text-white">{delivery.deliveryAgent.name} (Direct Line: {delivery.deliveryAgent.phone})</p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-[#0a0a0a] border border-white/5 p-6">
        <h3 className="text-[10px] tracking-widest uppercase text-white mb-6 flex items-center gap-4">
          Transmission Log <span className="flex-1 h-[1px] bg-white/10"></span>
        </h3>
        <div className="space-y-6 max-h-64 overflow-y-auto scrollbar-hide">
          {delivery.trackingUpdates?.slice().reverse().map((update, index) => (
            <div key={index} className="relative pl-6 border-l border-white/10 pb-2 last:border-transparent">
              <div className="absolute top-1 -left-[5px] w-2 h-2 rounded-full bg-gold"></div>
              <p className="text-[10px] text-textMuted uppercase tracking-widest mb-1">{new Date(update.timestamp).toLocaleString()}</p>
              <p className="text-sm text-gold font-light mb-1">{update.status}</p>
              <p className="text-xs text-white/70">{update.location}</p>
              {update.notes && <p className="text-[10px] text-textMuted mt-1 border-l-2 border-white/10 pl-2 italic">{update.notes}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DeliveryTracker;
